import { useMutation, useQueryClient } from '@tanstack/react-query'
import AppService from '@services/AppService'
import { columnCardsQueryKey } from './useColumnCards'

function useBoardMutations(boardId) {
  const queryClient = useQueryClient()

  const invalidateBoard = () => {
    queryClient.invalidateQueries({ queryKey: ['kanbanBoard', boardId] })
    queryClient.invalidateQueries({ queryKey: ['kanbanBoards'] })
  }

  const invalidateBoardOnly = () => {
    queryClient.invalidateQueries({ queryKey: ['kanbanBoard', boardId] })
  }

  const invalidateColumnCards = (columnId) => {
    if (columnId == null) return
    queryClient.invalidateQueries({ queryKey: columnCardsQueryKey(columnId) })
  }

  const invalidateFacets = () => {
    if (boardId == null) return
    queryClient.invalidateQueries({ queryKey: ['kanbanBoardFacets', boardId] })
    queryClient.invalidateQueries({ queryKey: ['kanbanBoardSearch', boardId] })
  }

  const createBoard = useMutation({
    mutationFn: (data) => AppService.kanbanBoards.create({ kanban_board: data }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kanbanBoards'] })
    }
  })

  const updateBoard = useMutation({
    mutationFn: (data) => AppService.kanbanBoards.update(boardId, { kanban_board: data }),
    onSuccess: invalidateBoard
  })

  const deleteBoard = useMutation({
    mutationFn: () => {
      const { FetchRequest } = require('@rails/request.js')
      const request = new FetchRequest('delete', `/kanban_boards/${boardId}`, {
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' }
      })
      return request.perform().then(r => r.json)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kanbanBoards'] })
    }
  })

  const setDefaultBoard = useMutation({
    mutationFn: () => {
      const { FetchRequest } = require('@rails/request.js')
      const request = new FetchRequest('put', `/kanban_boards/${boardId}/set_default`, {
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' }
      })
      return request.perform().then(r => r.json)
    }
  })

  const createColumn = useMutation({
    mutationFn: (data) => {
      const { FetchRequest } = require('@rails/request.js')
      const request = new FetchRequest('post', `/kanban_boards/${boardId}/kanban_columns`, {
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({ kanban_column: data })
      })
      return request.perform().then(r => r.json)
    },
    onSuccess: invalidateBoard
  })

  const updateColumn = useMutation({
    mutationFn: ({ id, ...data }) => AppService.kanbanColumns.update(id, { kanban_column: data }),
    onMutate: async ({ id, row_order_position }) => {
      if (row_order_position == null) return
      await queryClient.cancelQueries({ queryKey: ['kanbanBoard', boardId] })
      const previous = queryClient.getQueryData(['kanbanBoard', boardId])

      if (previous?.board) {
        queryClient.setQueryData(['kanbanBoard', boardId], old => {
          if (!old?.board) return old
          const columns = [...old.board.columns]
          const fromIdx = columns.findIndex(c => c.id === id)
          if (fromIdx === -1) return old
          const [col] = columns.splice(fromIdx, 1)
          const toIdx = typeof row_order_position === 'number' ? row_order_position : columns.length
          columns.splice(toIdx, 0, col)
          return { ...old, board: { ...old.board, columns } }
        })
      }

      return { previous }
    },
    onError: (err, vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(['kanbanBoard', boardId], context.previous)
      }
    },
    onSettled: invalidateBoardOnly
  })

  const deleteColumn = useMutation({
    mutationFn: (id) => {
      const { FetchRequest } = require('@rails/request.js')
      const request = new FetchRequest('delete', `/kanban_columns/${id}`, {
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' }
      })
      return request.perform().then(r => r.json)
    },
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: ['kanbanBoard', boardId] })
      const snapshot = queryClient.getQueryData(['kanbanBoard', boardId])
      queryClient.setQueryData(['kanbanBoard', boardId], (old) => {
        if (!old?.board) return old
        return {
          ...old,
          board: {
            ...old.board,
            columns: old.board.columns.filter(c => c.id !== id)
          }
        }
      })
      return { snapshot }
    },
    onError: (_err, _vars, context) => {
      if (context?.snapshot) {
        queryClient.setQueryData(['kanbanBoard', boardId], context.snapshot)
      }
    },
    onSettled: invalidateBoard
  })

  const clearColumnCards = useMutation({
    mutationFn: (columnId) => {
      const { FetchRequest } = require('@rails/request.js')
      const request = new FetchRequest('delete', `/kanban_columns/${columnId}/clear_cards`, {
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' }
      })
      return request.perform().then(r => r.json)
    },
    onMutate: async (columnId) => {
      const prefix = columnCardsQueryKey(columnId)
      await queryClient.cancelQueries({ queryKey: prefix })
      const snapshots = queryClient.getQueriesData({ queryKey: prefix })
      queryClient.setQueriesData({ queryKey: prefix }, (old) => {
        if (!old?.pages) return old
        return {
          ...old,
          pages: [{ cards: [], total_count: 0, next_cursor: null, unseen_count: 0 }],
          pageParams: [null]
        }
      })
      return { snapshots }
    },
    onError: (_err, _vars, context) => {
      context?.snapshots?.forEach(([key, data]) => queryClient.setQueryData(key, data))
    },
    onSuccess: () => {
      invalidateFacets()
    }
  })

  const createCard = useMutation({
    mutationFn: (data) => {
      const { FetchRequest } = require('@rails/request.js')
      const request = new FetchRequest('post', `/kanban_boards/${boardId}/kanban_cards`, {
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({ kanban_card: data })
      })
      return request.perform().then(r => r.json)
    },
    onSuccess: (_data, vars) => {
      invalidateColumnCards(vars.kanban_column_id)
      invalidateFacets()
    }
  })

  const updateCard = useMutation({
    mutationFn: ({ id, _sourceColumnId, ...data }) => AppService.kanbanCards.update(id, { kanban_card: data }),
    onMutate: async ({ id, kanban_column_id, row_order_position, _sourceColumnId }) => {
      const sourceColumnId = _sourceColumnId
      const targetColumnId = kanban_column_id ?? sourceColumnId

      const normalKey = (cid) => [...columnCardsQueryKey(cid), null]
      const cancelKeys = [columnCardsQueryKey(sourceColumnId)]
      if (targetColumnId !== sourceColumnId) cancelKeys.push(columnCardsQueryKey(targetColumnId))
      await Promise.all(cancelKeys.map(k => queryClient.cancelQueries({ queryKey: k })))

      const dataKeys = [normalKey(sourceColumnId)]
      if (targetColumnId !== sourceColumnId) dataKeys.push(normalKey(targetColumnId))
      const snapshots = dataKeys.map(k => [k, queryClient.getQueryData(k)])

      const removeFrom = (data) => {
        if (!data?.pages) return data
        let removed = null
        const pages = data.pages.map(p => {
          const idx = p.cards.findIndex(c => c.id === id)
          if (idx === -1) return p
          const cards = [...p.cards]
          removed = cards.splice(idx, 1)[0]
          return { ...p, cards, total_count: Math.max(0, (p.total_count || 0) - 1) }
        })
        return { snapshot: { ...data, pages }, removed }
      }

      const insertInto = (data, card, position) => {
        if (!data?.pages?.length) return data
        const pages = [...data.pages]
        const first = { ...pages[0], cards: [...pages[0].cards] }
        const pos = typeof position === 'number' ? Math.min(position, first.cards.length) : first.cards.length
        first.cards.splice(pos, 0, { ...card, kanban_column_id: targetColumnId })
        first.total_count = (first.total_count || 0) + 1
        pages[0] = first
        return { ...data, pages }
      }

      let movedCard = null
      const sourceData = queryClient.getQueryData(normalKey(sourceColumnId))
      const removed = removeFrom(sourceData)
      if (removed?.snapshot) {
        queryClient.setQueryData(normalKey(sourceColumnId), removed.snapshot)
        movedCard = removed.removed
      }

      if (movedCard && targetColumnId != null) {
        const targetData = queryClient.getQueryData(normalKey(targetColumnId))
        const updated = insertInto(targetData, movedCard, row_order_position)
        if (updated) queryClient.setQueryData(normalKey(targetColumnId), updated)
      }

      return { snapshots }
    },
    onError: (err, vars, context) => {
      context?.snapshots?.forEach(([key, data]) => queryClient.setQueryData(key, data))
    },
    onSettled: (_data, _err, vars) => {
      invalidateColumnCards(vars._sourceColumnId)
      if (vars.kanban_column_id && vars.kanban_column_id !== vars._sourceColumnId) {
        invalidateColumnCards(vars.kanban_column_id)
      }
      invalidateFacets()
    }
  })

  const deleteCard = useMutation({
    mutationFn: ({ id }) => {
      const { FetchRequest } = require('@rails/request.js')
      const request = new FetchRequest('delete', `/kanban_cards/${id}`, {
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' }
      })
      return request.perform().then(r => r.json)
    },
    onSuccess: (_data, vars) => {
      invalidateColumnCards(vars.columnId)
      invalidateFacets()
    }
  })

  const createCollaborator = useMutation({
    mutationFn: (data) => {
      const { FetchRequest } = require('@rails/request.js')
      const request = new FetchRequest('post', `/kanban_boards/${boardId}/kanban_board_collaborators`, {
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({ kanban_board_collaborator: data })
      })
      return request.perform().then(r => r.json)
    },
    onSuccess: invalidateBoard
  })

  const updateCollaborator = useMutation({
    mutationFn: ({ id, ...data }) => AppService.kanbanBoardCollaborators.update(id, { kanban_board_collaborator: data }),
    onSuccess: invalidateBoard
  })

  const deleteCollaborator = useMutation({
    mutationFn: (id) => {
      const { FetchRequest } = require('@rails/request.js')
      const request = new FetchRequest('delete', `/kanban_board_collaborators/${id}`, {
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' }
      })
      return request.perform().then(r => r.json)
    },
    onSuccess: invalidateBoard
  })

  const markCardRead = useMutation({
    mutationFn: ({ cardId }) => {
      const { FetchRequest } = require('@rails/request.js')
      const request = new FetchRequest('put', `/kanban_cards/${cardId}/mark_read`, {
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' }
      })
      return request.perform().then(r => r.json)
    },
    onSuccess: (_data, vars) => {
      invalidateColumnCards(vars.columnId)
      queryClient.invalidateQueries({ queryKey: ['kanbanBoards'] })
    }
  })

  const markColumnRead = useMutation({
    mutationFn: (columnId) => {
      const { FetchRequest } = require('@rails/request.js')
      const request = new FetchRequest('put', `/kanban_columns/${columnId}/mark_read`, {
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' }
      })
      return request.perform().then(r => r.json)
    },
    onSuccess: (_data, columnId) => {
      invalidateColumnCards(columnId)
      queryClient.invalidateQueries({ queryKey: ['kanbanBoards'] })
    }
  })

  const markBoardRead = useMutation({
    mutationFn: () => {
      const { FetchRequest } = require('@rails/request.js')
      const request = new FetchRequest('put', `/kanban_boards/${boardId}/mark_all_read`, {
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' }
      })
      return request.perform().then(r => r.json)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kanbanColumn'] })
      queryClient.invalidateQueries({ queryKey: ['kanbanBoards'] })
    }
  })

  return {
    createBoard,
    updateBoard,
    deleteBoard,
    setDefaultBoard,
    createColumn,
    updateColumn,
    deleteColumn,
    clearColumnCards,
    createCard,
    updateCard,
    deleteCard,
    createCollaborator,
    updateCollaborator,
    deleteCollaborator,
    markCardRead,
    markColumnRead,
    markBoardRead
  }
}

export default useBoardMutations
